import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import { useAuth } from './AuthContext'
import { C, FONT } from './theme'
import { CopyButton } from './CopyButton'
import {
  Activity, Circle, Monitor, Smartphone, Tablet, Globe, RefreshCw,
  ShieldOff, Clock, User, Search, MapPin, ChevronLeft, ChevronRight,
  Calendar, Unlock,
} from 'lucide-react'

const fmtTempo = (data) => {
  const t = new Date(data).getTime()
  const diff = Math.max(0, Date.now() - t)
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'agora'
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d`
  return new Date(data).toLocaleDateString('pt-PT')
}

const fmtDataHora = (data) => new Date(data).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })

const DeviceIcon = ({ device, size = 14 }) => {
  if (device === 'Mobile') return <Smartphone size={size} />
  if (device === 'Tablet') return <Tablet size={size} />
  return <Monitor size={size} />
}

export default function Atividade() {
  const { user, isAdmin } = useAuth()
  const [tab, setTab] = useState('online')

  // Online (presence)
  const [online, setOnline] = useState([])

  // ── Libertacoes ───────────────────────────────────────────────────────────
  // Ate agora so se via o historico DE UM produto, no ecra de Gestao de Stock, e
  // so as libertacoes manuais. Isso obriga a saber de antemao qual e o artigo —
  // e quando o Marco procurou um La Mer que tinha libertado dias antes, era
  // precisamente isso que ele nao sabia.
  //
  // Aqui estao todas, as manuais e as automaticas, e procura-se por nome, EAN
  // ou email de quem era o dono.
  const [libertacoes, setLibertacoes] = useState([])
  const [filtroLib, setFiltroLib] = useState('')
  useEffect(() => {
    if (!isAdmin || tab !== 'libertacoes') return
    supabase.from('historico_libertacao_produtos')
      .select('id, libertado_em, origem, produto_nome, produto_ean, ex_dono_email, ex_dono_nome, libertado_por')
      .order('libertado_em', { ascending: false })
      .limit(500)
      .then(({ data }) => setLibertacoes(data || []))
  }, [isAdmin, tab])

  const libertacoesFiltradas = useMemo(() => {
    const q = filtroLib.trim().toLowerCase()
    if (!q) return libertacoes
    return libertacoes.filter(l =>
      String(l.produto_nome || '').toLowerCase().includes(q) ||
      String(l.produto_ean || '').includes(q) ||
      String(l.ex_dono_email || '').toLowerCase().includes(q) ||
      String(l.ex_dono_nome || '').toLowerCase().includes(q))
  }, [libertacoes, filtroLib])
  // Historico
  const [historico, setHistorico] = useState([])
  const [historicoTotal, setHistoricoTotal] = useState(0)
  const [filtroEmail, setFiltroEmail] = useState('')
  const [pagina, setPagina] = useState(0)
  const PAGE_SIZE = 25
  // Por user (agregado)
  const [resumo, setResumo] = useState([])
  // Timeline (agrupado por dia)
  const [timeline, setTimeline] = useState([])
  const [diasMostrar, setDiasMostrar] = useState(7)
  const [loading, setLoading] = useState(false)

  // Online via presence channel (read-only watcher do mesmo channel "online-users")
  useEffect(() => {
    if (!isAdmin || !user) return
    let ch
    try {
      ch = supabase.channel('online-users', { config: { presence: { key: `watcher-${user.id}` } } })
      ch.on('presence', { event: 'sync' }, () => {
        try {
          const state = ch.presenceState() || {}
          // UMA LINHA POR PESSOA, e nao por ligacao.
          //
          // O presenceState devolve um array por chave, e a chave e o user_id:
          // duas abas abertas do mesmo utilizador sao duas entradas. Estavam a
          // ser mostradas como duas pessoas — o Marco viu-se a si proprio em
          // duplicado, uma vez no dashboard e outra no catalogo.
          //
          // Fica a entrada mais recente de cada um, e o numero de abas ao lado.
          const porUtilizador = new Map()
          for (const key in state) {
            for (const entry of (state[key] || [])) {
              if (!entry?.path || !entry?.user_id) continue
              const anterior = porUtilizador.get(entry.user_id)
              const separadores = (anterior?.separadores || 0) + 1
              // Uma entrada COM nome ganha sempre a uma sem, mesmo sendo mais
              // antiga: a presenca e anunciada assim que a ligacao abre, e nessa
              // altura o perfil ainda nao carregou — a primeira aba a ligar-se
              // costuma anunciar-se so com o email. Sem esta regra, a pessoa
              // aparecia identificada pelo email em vez do nome.
              const melhor = !anterior ? entry
                : (!anterior.nome && entry.nome) ? entry
                : (anterior.nome && !entry.nome) ? anterior
                : ((entry.since || '') > (anterior.since || '') ? entry : anterior)
              porUtilizador.set(entry.user_id, { ...melhor, separadores })
            }
          }
          const flat = [...porUtilizador.values()]
          flat.sort((a, b) => (b.is_admin ? 1 : 0) - (a.is_admin ? 1 : 0))
          setOnline(flat)
        } catch { /* silencioso */ }
      })
      ch.subscribe()
    } catch { /* silencioso */ }
    return () => { try { if (ch) supabase.removeChannel(ch) } catch { /* silencioso */ } }
  }, [isAdmin, user?.id])

  // Carregar historico (paginado + filtro)
  const carregarHistorico = async () => {
    if (!isAdmin) return
    setLoading(true)
    let q = supabase.from('visitas')
      .select('id, user_id, email, nome, ip, browser, os, device, path, created_at', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(pagina * PAGE_SIZE, (pagina + 1) * PAGE_SIZE - 1)
    if (filtroEmail.trim()) q = q.ilike('email', `%${filtroEmail.trim()}%`)
    const { data, count, error } = await q
    if (!error) {
      setHistorico(data || [])
      setHistoricoTotal(count || 0)
    }
    setLoading(false)
  }

  useEffect(() => { if (tab === 'historico') carregarHistorico() }, [tab, pagina, filtroEmail])

  // Carregar resumo por user
  const carregarResumo = async () => {
    if (!isAdmin) return
    setLoading(true)
    // Buscar todos os visitas com agregacao manual (chega para volumes pequenos < 50k)
    const { data } = await supabase.from('visitas')
      .select('user_id, email, nome, ip, browser, os, device, created_at')
      .order('created_at', { ascending: false })
      .limit(2000)
    const mapa = new Map()
    for (const v of (data || [])) {
      if (!v.user_id) continue
      const key = v.user_id
      const ent = mapa.get(key) || {
        user_id: v.user_id, email: v.email, nome: v.nome,
        primeiro: v.created_at, ultimo: v.created_at,
        total: 0, ips: new Set(), devices: new Set(),
        horas: new Array(24).fill(0),
      }
      ent.total++
      if (v.ip) ent.ips.add(v.ip)
      if (v.device) ent.devices.add(`${v.os || ''} ${v.browser || ''} ${v.device}`.trim())
      const ts = new Date(v.created_at)
      if (ts > new Date(ent.ultimo)) ent.ultimo = v.created_at
      if (ts < new Date(ent.primeiro)) ent.primeiro = v.created_at
      const h = ts.getHours()
      ent.horas[h] = (ent.horas[h] || 0) + 1
      mapa.set(key, ent)
    }
    const lista = Array.from(mapa.values())
      .map(r => ({ ...r, ips: Array.from(r.ips), devices: Array.from(r.devices) }))
      .sort((a, b) => new Date(b.ultimo) - new Date(a.ultimo))
    setResumo(lista)
    setLoading(false)
  }

  useEffect(() => { if (tab === 'resumo') carregarResumo() }, [tab])

  // Carregar timeline (ultimos N dias, agrupado por dia)
  const carregarTimeline = async () => {
    if (!isAdmin) return
    setLoading(true)
    const desdeISO = new Date(Date.now() - diasMostrar * 86400000).toISOString()
    const { data } = await supabase.from('visitas')
      .select('id, user_id, email, nome, ip, browser, os, device, path, created_at')
      .gte('created_at', desdeISO)
      .order('created_at', { ascending: false })
      .limit(1000)
    // Agrupar por dia (chave YYYY-MM-DD)
    const grupos = new Map()
    for (const v of (data || [])) {
      const d = new Date(v.created_at)
      const chave = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      const grupo = grupos.get(chave) || { dia: chave, dataObj: d, visitas: [] }
      grupo.visitas.push(v)
      grupos.set(chave, grupo)
    }
    const lista = Array.from(grupos.values()).sort((a, b) => b.dataObj - a.dataObj)
    setTimeline(lista)
    setLoading(false)
  }

  useEffect(() => { if (tab === 'timeline') carregarTimeline() }, [tab, diasMostrar])

  if (!isAdmin) {
    return (
      <div style={{ padding: '60px 28px', textAlign: 'center', color: C.danger, fontFamily: FONT }}>
        <ShieldOff size={48} style={{ margin: '0 auto 12px' }} />
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Acesso restrito</h2>
        <p style={{ margin: '6px 0 0', fontSize: '13px', color: C.textMuted }}>Esta página só está disponível para administradores.</p>
      </div>
    )
  }

  const totalPaginas = Math.max(1, Math.ceil(historicoTotal / PAGE_SIZE))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', fontFamily: FONT }}>
      {/* HEADER */}
      <div style={{ padding: '20px 28px 12px', background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: C.text, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Activity size={22} color={C.accent} /> Atividade
        </h1>
        <p style={{ margin: '4px 0 14px', fontSize: '13px', color: C.textMuted }}>
          Visitantes online, histórico de acessos e estatísticas por utilizador. Dados eliminados aos 90 dias (LGPD).
        </p>

        {/* TABS */}
        <div style={{ display: 'flex', gap: '4px', borderBottom: `1px solid ${C.border}`, marginTop: '8px' }}>
          {[
            { k: 'online', label: 'Online agora', icon: <Circle size={10} fill={C.success} color={C.success} /> },
            { k: 'historico', label: 'Histórico', icon: <Clock size={13} /> },
            { k: 'resumo', label: 'Por utilizador', icon: <User size={13} /> },
            { k: 'timeline', label: 'Timeline', icon: <Calendar size={13} /> },
            { k: 'libertacoes', label: 'Libertações', icon: <Unlock size={13} /> },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '13px', fontWeight: '700',
                color: tab === t.k ? C.text : C.textMuted,
                borderBottom: tab === t.k ? `2px solid ${C.accent}` : '2px solid transparent',
                marginBottom: '-1px',
              }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* CONTEUDO */}
      <div style={{ padding: '20px 28px', flex: 1 }}>
        {tab === 'online' && (
          <div>
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: C.textMuted }}>
              <Circle size={8} fill={C.success} color={C.success} />
              <span><strong style={{ color: C.text }}>{online.length}</strong> utilizador{online.length === 1 ? '' : 'es'} online agora</span>
            </div>
            {online.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: C.textMuted }}>
                <Globe size={40} style={{ opacity: 0.25, marginBottom: '8px' }} />
                <p style={{ margin: 0, fontSize: '14px' }}>Ninguém online de momento.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {online.map((p, i) => (
                  <div key={`${p.user_id}-${i}`} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 14px', background: C.surface,
                    border: `1px solid ${C.border}`, borderRadius: '6px',
                  }}>
                    <Circle size={10} fill={C.success} color={C.success} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <strong style={{ fontSize: '14px', color: C.text }}>{p.nome || p.email || 'Anónimo'}</strong>
                        {p.is_admin && <span style={{ fontSize: '10px', fontWeight: '700', background: C.accent, color: C.accentText, padding: '2px 6px', borderRadius: '3px' }}>ADMIN</span>}
                      </div>
                      <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        {p.email}
                        {p.email && <CopyButton text={p.email} size={10} title="Copiar email" />}
                      </div>
                    </div>
                    <div style={{ fontSize: '12px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <MapPin size={12} /> {p.path || '—'}
                      {p.separadores > 1 && (
                        <span title="A mesma pessoa com mais do que um separador aberto"
                          style={{ fontSize: '10px', color: C.textFaint }}>
                          · {p.separadores} separadores
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: C.textFaint }}>desde {fmtTempo(p.since)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'historico' && (
          <div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
              <div style={{ flex: 1, position: 'relative', maxWidth: '320px' }}>
                <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: C.textMuted }} />
                <input value={filtroEmail} onChange={e => { setFiltroEmail(e.target.value); setPagina(0) }}
                  placeholder="Filtrar por email..."
                  style={{ width: '100%', padding: '8px 12px 8px 32px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <button onClick={carregarHistorico} disabled={loading}
                style={{ padding: '8px 12px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '4px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Recarregar
              </button>
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: C.textMuted }}>{historicoTotal} registos</span>
            </div>

            <div style={{ border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden', background: C.surface }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '700', fontSize: '11px', color: C.textMuted, textTransform: 'uppercase' }}>Utilizador</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '700', fontSize: '11px', color: C.textMuted, textTransform: 'uppercase' }}>IP</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '700', fontSize: '11px', color: C.textMuted, textTransform: 'uppercase' }}>Dispositivo</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '700', fontSize: '11px', color: C.textMuted, textTransform: 'uppercase' }}>Página</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700', fontSize: '11px', color: C.textMuted, textTransform: 'uppercase' }}>Quando</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.length === 0 ? (
                    <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>
                      {loading ? 'A carregar...' : 'Sem registos.'}
                    </td></tr>
                  ) : historico.map(v => (
                    <tr key={v.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: '600', color: C.text, display: 'flex', alignItems: 'center', gap: '2px' }}>
                          {v.nome || '—'}
                          {v.nome && <CopyButton text={v.nome} size={10} title="Copiar nome" />}
                        </div>
                        <div style={{ fontSize: '11px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '2px' }}>
                          {v.email}
                          {v.email && <CopyButton text={v.email} size={10} title="Copiar email" />}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '12px', color: C.textMuted }}>{v.ip || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <DeviceIcon device={v.device} />
                          <span style={{ fontSize: '12px' }}>{v.browser} · {v.os}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: '12px', color: C.textMuted }}>{v.path || '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px' }}>
                        <div style={{ color: C.text, fontWeight: '600' }}>{fmtDataHora(v.created_at)}</div>
                        <div style={{ color: C.textFaint, fontSize: '11px' }}>há {fmtTempo(v.created_at)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {historicoTotal > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '14px', fontSize: '13px' }}>
                <button onClick={() => setPagina(p => Math.max(0, p - 1))} disabled={pagina === 0}
                  style={{ background: C.surface, border: `1px solid ${C.border}`, padding: '6px 10px', borderRadius: '4px', cursor: pagina === 0 ? 'not-allowed' : 'pointer', opacity: pagina === 0 ? 0.4 : 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <ChevronLeft size={14} /> Anterior
                </button>
                <span style={{ color: C.textMuted }}>Página {pagina + 1} de {totalPaginas}</span>
                <button onClick={() => setPagina(p => Math.min(totalPaginas - 1, p + 1))} disabled={pagina >= totalPaginas - 1}
                  style={{ background: C.surface, border: `1px solid ${C.border}`, padding: '6px 10px', borderRadius: '4px', cursor: pagina >= totalPaginas - 1 ? 'not-allowed' : 'pointer', opacity: pagina >= totalPaginas - 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Próxima <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        )}

        {tab === 'resumo' && (
          <div>
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '13px', color: C.textMuted }}>
                <strong style={{ color: C.text }}>{resumo.length}</strong> utilizadores com actividade
              </span>
              <button onClick={carregarResumo} disabled={loading}
                style={{ marginLeft: 'auto', padding: '8px 12px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '4px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Recarregar
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
              {resumo.length === 0 && !loading && (
                <p style={{ color: C.textMuted, fontSize: '13px' }}>Sem dados ainda. Aguarde primeiras visitas.</p>
              )}
              {resumo.map(r => {
                const maxH = Math.max(...r.horas, 1)
                return (
                <div key={r.user_id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '14px' }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', color: C.text, display: 'flex', alignItems: 'center', gap: '2px' }}>
                    {r.nome || '—'}
                    {r.nome && <CopyButton text={r.nome} size={11} title="Copiar nome" />}
                  </div>
                  <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                    {r.email}
                    {r.email && <CopyButton text={r.email} size={10} title="Copiar email" />}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderTop: `1px solid ${C.border}`, paddingTop: '8px' }}>
                    <div>
                      <div style={{ color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>Visitas</div>
                      <div style={{ color: C.text, fontWeight: '700', fontSize: '15px' }}>{r.total}</div>
                    </div>
                    <div>
                      <div style={{ color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>IPs</div>
                      <div style={{ color: C.text, fontWeight: '700', fontSize: '15px' }}>{r.ips.length}</div>
                    </div>
                    <div>
                      <div style={{ color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>Última</div>
                      <div style={{ color: C.text, fontWeight: '700', fontSize: '13px' }} title={fmtDataHora(r.ultimo)}>{fmtTempo(r.ultimo)}</div>
                    </div>
                  </div>

                  {/* Primeiro / Último acesso (datetime exacto) */}
                  <div style={{ marginTop: '10px', borderTop: `1px dashed ${C.border}`, paddingTop: '8px', fontSize: '11px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: C.textMuted }}>
                      <span>1.º acesso</span>
                      <strong style={{ color: C.text }}>{fmtDataHora(r.primeiro)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: C.textMuted, marginTop: '3px' }}>
                      <span>Último acesso</span>
                      <strong style={{ color: C.text }}>{fmtDataHora(r.ultimo)}</strong>
                    </div>
                  </div>

                  {/* Barra de horas activas (0h-23h) */}
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ fontSize: '10px', color: C.textMuted, textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px' }}>Horas activas (0h-23h)</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1px', height: '24px', background: C.surfaceAlt, borderRadius: '3px', padding: '2px' }}>
                      {r.horas.map((count, h) => (
                        <div key={h}
                          title={`${String(h).padStart(2, '0')}h00 — ${count} ${count === 1 ? 'visita' : 'visitas'}`}
                          style={{
                            flex: 1,
                            height: count === 0 ? '2px' : `${Math.max(15, (count / maxH) * 100)}%`,
                            background: count === 0 ? C.border : C.accent,
                            borderRadius: '1px',
                          }} />
                      ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: C.textFaint, marginTop: '2px' }}>
                      <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
                    </div>
                  </div>

                  {r.ips.length > 0 && (
                    <div style={{ marginTop: '10px', fontSize: '11px', color: C.textMuted, fontFamily: 'monospace' }} title="IPs usados">
                      {r.ips.slice(0, 3).join(', ')}{r.ips.length > 3 && ` +${r.ips.length - 3}`}
                    </div>
                  )}
                  {r.devices.length > 0 && (
                    <div style={{ marginTop: '4px', fontSize: '11px', color: C.textMuted }} title="Dispositivos">
                      {r.devices.slice(0, 2).join(' · ')}{r.devices.length > 2 && ` +${r.devices.length - 2}`}
                    </div>
                  )}
                </div>
                )
              })}
            </div>
          </div>
        )}

        {tab === 'timeline' && (
          <div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', color: C.textMuted }}>Últimos</span>
              {[3, 7, 14, 30].map(n => (
                <button key={n} onClick={() => setDiasMostrar(n)}
                  style={{
                    padding: '6px 12px', border: `1px solid ${diasMostrar === n ? C.accent : C.border}`,
                    background: diasMostrar === n ? C.accent : C.surface,
                    color: diasMostrar === n ? C.accentText : C.text,
                    borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '700',
                  }}>
                  {n} dias
                </button>
              ))}
              <button onClick={carregarTimeline} disabled={loading}
                style={{ marginLeft: 'auto', padding: '6px 12px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '4px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Recarregar
              </button>
            </div>

            {timeline.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: C.textMuted, fontSize: '13px' }}>Sem visitas no período seleccionado.</div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {timeline.map(g => {
                const dt = g.dataObj
                const hoje = new Date()
                const ehHoje = dt.toDateString() === hoje.toDateString()
                const ehOntem = dt.toDateString() === new Date(hoje.getTime() - 86400000).toDateString()
                const labelDia = ehHoje ? 'Hoje' : ehOntem ? 'Ontem' : dt.toLocaleDateString('pt-PT', { weekday: 'long', day: '2-digit', month: 'long' })
                return (
                  <div key={g.dia}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <Calendar size={14} color={ehHoje ? C.accent : C.textMuted} />
                      <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: ehHoje ? C.text : C.textMuted, textTransform: 'capitalize' }}>
                        {labelDia}
                      </h3>
                      <span style={{ fontSize: '11px', color: C.textFaint }}>· {g.visitas.length} {g.visitas.length === 1 ? 'visita' : 'visitas'}</span>
                    </div>
                    <div style={{ borderLeft: `2px solid ${ehHoje ? C.accent : C.border}`, marginLeft: '6px', paddingLeft: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {g.visitas.map(v => {
                        const hh = new Date(v.created_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })
                        return (
                          <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', padding: '4px 0' }}>
                            <span style={{ fontFamily: 'monospace', color: C.text, fontWeight: '700', minWidth: '40px' }}>{hh}</span>
                            <span style={{ color: C.text, fontWeight: '600' }}>{v.nome || v.email}</span>
                            <span style={{ color: C.textMuted, fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <MapPin size={11} /> {v.path || '—'}
                            </span>
                            <span style={{ color: C.textFaint, fontSize: '11px', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <DeviceIcon device={v.device} size={11} />
                              {v.ip || '—'}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {tab === 'libertacoes' && (
          <div>
            <p style={{ margin: '0 0 12px', fontSize: '12px', color: C.textMuted, lineHeight: 1.5 }}>
              Artigos que deixaram de estar associados a um cliente. As <strong>automáticas</strong> são
              soltas ao fim de 15 dias sem stock nem trânsitos; as <strong>manuais</strong> foram
              desassociadas no formulário do produto.
            </p>

            <div style={{ position: 'relative', marginBottom: '12px', maxWidth: '460px' }}>
              <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: C.textMuted }} />
              <input
                value={filtroLib}
                onChange={e => setFiltroLib(e.target.value)}
                placeholder="Procurar por nome, EAN ou email do ex-dono…"
                style={{
                  width: '100%', padding: '8px 10px 8px 32px', fontSize: '13px',
                  border: `1px solid ${C.border}`, borderRadius: '6px',
                  color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: FONT,
                }} />
            </div>

            {libertacoesFiltradas.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px 20px', color: C.textMuted }}>
                <Unlock size={36} style={{ opacity: 0.25, marginBottom: '8px' }} />
                <p style={{ margin: 0, fontSize: '14px' }}>
                  {filtroLib ? 'Nada encontrado com esse termo.' : 'Ainda não houve libertações.'}
                </p>
              </div>
            ) : (
              <div style={{ border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden' }}>
                {libertacoesFiltradas.map((l, i) => (
                  <div key={l.id} style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px',
                    background: i % 2 === 0 ? C.surface : C.surfaceAlt,
                    borderBottom: i < libertacoesFiltradas.length - 1 ? `1px solid ${C.border}` : 'none',
                  }}>
                    <span style={{
                      fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px',
                      padding: '3px 7px', borderRadius: '3px', whiteSpace: 'nowrap',
                      background: l.origem === 'manual' ? C.warningSoft : C.surfaceAlt,
                      color: l.origem === 'manual' ? C.warning : C.textMuted,
                      border: `1px solid ${l.origem === 'manual' ? `${C.warning}55` : C.border}`,
                    }}>{l.origem === 'manual' ? 'manual' : 'auto'}</span>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: C.text }}>{l.produto_nome}</div>
                      <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        {/* O EAN e o que serve para procurar o artigo meses depois — pelo
                            nome obriga a adivinhar como o fornecedor o escreveu. */}
                        {l.produto_ean && <>
                          <span style={{ fontFamily: 'monospace' }}>{l.produto_ean}</span>
                          <CopyButton text={String(l.produto_ean)} size={10} title="Copiar EAN" />
                          <span style={{ color: C.textFaint }}>·</span>
                        </>}
                        <span>era de <strong style={{ color: C.text }}>{l.ex_dono_nome || l.ex_dono_email}</strong></span>
                        {l.libertado_por && <>
                          <span style={{ color: C.textFaint }}>·</span>
                          <span>libertado por {l.libertado_por}</span>
                        </>}
                      </div>
                    </div>

                    <div style={{ fontSize: '11px', color: C.textMuted, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {new Date(l.libertado_em).toLocaleDateString('pt-PT')}
                      <div style={{ color: C.textFaint }}>{fmtTempo(l.libertado_em)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p style={{ margin: '10px 0 0', fontSize: '11px', color: C.textFaint }}>
              {libertacoesFiltradas.length} de {libertacoes.length} libertações
            </p>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
